import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Paper,
  Grid,
} from "@mui/material";
import { Alert } from "@mui/material";
import { useRefreshTimer, useSpecificLocationData } from "../../hooks";
import {
  SelectedLocationForMove,
  Location,
  SpecialLocation,
} from "../../models";
import { LocationSection } from "./move-billets-modal/components";
import { clearLocation } from "../../repository/BilletYardInventoryRepository";
import { API_CONSTANTS } from "./edit-location-modal/constants/locationContentEdition";
import { ClearConfirmationModal } from "./edit-location-modal/components/ClearConfirmationModal";

// Utility function to get location display text
const getLocationDisplayText = (location: Location | SpecialLocation) => {
  return location.HeaderText;
};

interface ClearLocationModalProps {
  show: boolean;
  onHide: () => void;
  locationId?: number | null;
  selectedCraneId: number;
  refreshInterval?: number;
}

export const ClearLocationModal = ({
  show,
  onHide,
  locationId,
  selectedCraneId,
  refreshInterval = 5,
}: ClearLocationModalProps) => {
  const [isOperating, setIsOperating] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Hooks
  const {
    location: currentLocation,
    groups: currentGroups,
    isLoading,
    hasError,
    errorMessage,
    refreshLocationData,
  } = useSpecificLocationData({ locationId });

  useRefreshTimer(refreshInterval, refreshLocationData);

  // Prepare location data for LocationSection component
  const prepareLocationData = (): SelectedLocationForMove | null => {
    if (!currentLocation || !currentGroups || currentGroups.length === 0) {
      return null;
    }

    return {
      location: currentLocation,
      groups: currentGroups,
      selectedGroupIndex: null,
      selectedBilletsCount: 0,
      role: null,
    };
  };

  // Handle Clear
  const handleClearConfirm = async () => {
    // Early validation
    if (!currentLocation || !locationId || isOperating) return;

    try {
      // Set loading state
      setIsOperating(true);
      setOperationError("");

      // Call repository directly
      const response = await clearLocation(
        locationId,
        selectedCraneId,
        API_CONSTANTS.CHECK_VALIDATION,
      );

      // Handle response
      if (response.ok) {
        // Check for warning/error messages in the response
        const tables = response.data?.tables;
        if (
          tables &&
          tables.length > 0 &&
          tables[0].rows &&
          tables[0].rows.length > 0
        ) {
          const firstRow = tables[0].rows[0];
          const statusCode = firstRow.StatusCode;
          const message = firstRow.Message;

          if (statusCode === "F" || statusCode === "R") {
            setOperationError(message || "Operation failed");
            return;
          }
        }

        // Success handling
        setOperationError("");

        // Close modal after successful clear
        onHide();
      } else {
        setOperationError(
          response.message || "Clear location operation failed",
        );
      }
    } catch (error: unknown) {
      setOperationError(
        error instanceof Error ? error.message : "Failed to clear location",
      );
    } finally {
      setIsOperating(false);
    }
  };

  // Reset form state when modal shows/closes
  useEffect(() => {
    if (show) {
      setOperationError(null);
      setShowClearConfirm(false);
    }
  }, [show, locationId]);

  const handleClose = (
    _event: {},
    reason: "backdropClick" | "escapeKeyDown",
  ) => {
    if (
      isOperating &&
      (reason === "backdropClick" || reason === "escapeKeyDown")
    ) {
      return;
    }
    onHide();
  };

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      fullScreen
      disableEscapeKeyDown={isOperating}
    >
      <DialogTitle
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          padding: (theme) => theme.spacing(1.5, 3),
        }}
      >
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontWeight: "bold",
            color: (theme) => theme.palette.primary.contrastText,
            textAlign: "center",
            fontSize: "20px",
          }}
        >
          Clear Location
          {currentLocation && ` - ${currentLocation.HeaderText}`}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          height: "calc(100vh - 120px)",
          padding: (theme) => theme.spacing(2),
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {operationError && (
          <Alert
            severity="error"
            sx={{
              marginBottom: (theme) => theme.spacing(2),
              flexShrink: 0,
            }}
          >
            {operationError}
          </Alert>
        )}

        {hasError ? (
          <Alert
            severity="error"
            sx={{
              marginBottom: (theme) => theme.spacing(2),
              flexShrink: 0,
            }}
          >
            Failed to load location data: {errorMessage}
          </Alert>
        ) : isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
            }}
          >
            <CircularProgress size={60} />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ marginTop: "1rem" }}
            >
              Loading location data...
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              opacity: isOperating ? 0.5 : 1,
              pointerEvents: isOperating ? "none" : "auto",
              transition: "opacity 0.3s ease",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {/* Material UI Grid with centered column */}
            <Grid
              container
              spacing={1}
              sx={{
                height: "100%",
                margin: 0,
                padding: 0,
              }}
            >
              {/* Spacer Column */}
              <Grid item xs={4} />

              {/* Center Column - Location Display */}
              <Grid
                item
                xs={4}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                    padding: "6px",
                    borderRadius: "5px 5px 0 0",
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "16px",
                    flexShrink: 0,
                    marginBottom: "16px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "white",
                      margin: 0,
                    }}
                  >
                    {currentLocation
                      ? getLocationDisplayText(currentLocation)
                      : "Location"}
                  </Typography>
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    background: (theme) =>
                      `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: (theme) => theme.shape.borderRadius * 1.5,
                    padding: (theme) => theme.spacing(2),
                    boxShadow: (theme) => theme.shadows[2],
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflow: "auto",
                  }}
                >
                  <LocationSection
                    location={prepareLocationData()}
                    fallbackLocation={currentLocation}
                    getLocationDisplayText={getLocationDisplayText}
                    showEmptyState={true}
                  />
                </Paper>
              </Grid>

              {/* Spacer Column */}
              <Grid item xs={4} />
            </Grid>

            {/* Operation Loader Overlay */}
            {isOperating && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: (theme) => `rgba(255, 255, 255, 0.8)`,
                  zIndex: 1000,
                }}
              >
                <CircularProgress size={60} />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ marginTop: "1rem" }}
                >
                  Clearing location...
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: (theme) => theme.spacing(3),
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* Close Button */}
        <Button
          onClick={onHide}
          disabled={isOperating}
          variant="contained"
          size="large"
          sx={{
            minWidth: "300px",
            minHeight: "64px",
            fontSize: "20px",
            fontWeight: "bold",
            padding: (theme) => theme.spacing(2, 6),
            borderRadius: (theme) => theme.shape.borderRadius,
            backgroundColor: (theme) => theme.palette.grey[100],
            color: (theme) => theme.palette.text.primary,
            border: (theme) => `1px solid ${theme.palette.grey[300]}`,
            boxShadow: (theme) => theme.shadows[4],
            transition: "all 0.2s ease",
            marginRight: "16px",
          }}
        >
          Close
        </Button>

        {/* Clear Button - Same size as Close button */}
        <Button
          onClick={() => setShowClearConfirm(true)}
          disabled={isOperating || !currentGroups || currentGroups.length === 0}
          variant="contained"
          size="large"
          sx={{
            minWidth: "300px",
            minHeight: "64px",
            fontSize: "20px",
            fontWeight: "bold",
            padding: "16px 48px",
            borderRadius: "4px",
            backgroundColor: (theme) =>
              isOperating ? undefined : theme.palette.error.main,
            color: (theme) => theme.palette.error.contrastText,
            boxShadow: (theme) => theme.shadows[4],
            transition: "all 0.2s ease",
          }}
        >
          {isOperating ? (
            <CircularProgress
              size={24}
              sx={{ color: (theme) => theme.palette.error.contrastText }}
            />
          ) : (
            "Clear"
          )}
        </Button>
      </DialogActions>

      {/* Clear Confirmation Modal */}
      <ClearConfirmationModal
        open={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={handleClearConfirm}
        locationName={currentLocation?.HeaderText || ""}
        isLoading={isOperating}
      />
    </Dialog>
  );
};
